import {AggregationType, KpiVisibilityFlag} from '@hconnect/common/types'
import {FeatureFlagKey, useCommonFeatureFlag} from '@hproduce/feature-flags'
import React, {FC, useEffect, useMemo, useState} from 'react'
import {useForm, useWatch} from 'react-hook-form'
import {useTranslation} from 'react-i18next'

import {useEditKpiUnit, useEditKpiVisibilityFlags, useKpiUnits} from '../../hooks'
import {useEditKpiAggregationType} from '../../hooks/useEditKpiAggregationType'
import {useEditKpiWeight} from '../../hooks/useEditKpiWeight'
import {useKpis} from '../../hooks/useKpis'
import {
  KpiAggregationTypeOption,
  KpiUnitOption,
  KpiWeight,
  KpiFormProps,
  KpiFormField
} from '../../types'
import {getVisibilityOptions} from '../../utils'
import {mapAggregationTypesToOptions} from '../../utils/aggregationTypes'

import {KpiFormFields} from './KpiFormFields'

import {useUrlParam} from '@settings/modules/routing'

const mapUnitsToOptions = (units: string[]): KpiUnitOption[] =>
  units.map((unit) => ({
    id: unit,
    label: unit
  }))

const CONFIG = [
  {name: KpiFormField.KpiDefinitionId},
  {name: KpiFormField.KpiName},
  {name: KpiFormField.KpiMainEquipment},
  {name: KpiFormField.Unit},
  {name: KpiFormField.AggregationType},
  {name: KpiFormField.WeightKpi},
  {name: KpiFormField.KpiVisibilityFlags}
] as const

export const KpiForm: FC<KpiFormProps> = ({
  kpiTagname,
  kpiId,
  kpiUnit,
  kpiVisibilityFlags,
  kpiAggregationType,
  weightKpi
}) => {
  const {t} = useTranslation()
  const plantCode = useUrlParam('plantCode')
  const {data} = useKpis()

  const isAggregationTypeOption = useCommonFeatureFlag(FeatureFlagKey.KpiAggregationTypeOption, {
    plantId: plantCode
  })
  const [units, setUnits] = useState<KpiUnitOption[]>([])
  const [aggregationTypes] = useState<KpiAggregationTypeOption[]>(
    mapAggregationTypesToOptions(
      Object.values(AggregationType),
      t,
      isAggregationTypeOption ?? false
    )
  )
  const unitsResponse = useKpiUnits(kpiId)

  useEffect(() => {
    setUnits(mapUnitsToOptions(unitsResponse.data ?? []))
  }, [unitsResponse.data])

  const visibilityOptions = useMemo(() => getVisibilityOptions(t), [t])
  const weightKpiOptions = (data?.processStages[0].definitionsWithResults ?? []).map((item) => ({
    id: String(item.definition.kpiAssignment.id),
    label: item.definition.tagname
  }))

  const unitsList: string[] = unitsResponse.data || []
  const {mutate: editKpiUnit} = useEditKpiUnit()
  const {mutate: editKpiAggregationType} = useEditKpiAggregationType()
  const {mutate: editKpiWeight} = useEditKpiWeight()
  const {mutate: editKpiVisibilityFlags} = useEditKpiVisibilityFlags()
  const {
    handleSubmit,
    control,
    reset,
    setValue,
    formState: {dirtyFields}
  } = useForm<{
    kpiDefinitionId: number
    kpiName: string
    kpiMainEquipment: string
    unit: KpiUnitOption | null
    aggregationType: KpiAggregationTypeOption | null
    weightKpi: KpiWeight | null
    plantEntryPointVisibility: boolean
    kpiVisibilityFlags: KpiVisibilityFlag[]
  }>({
    mode: 'onChange',
    shouldFocusError: false,
    defaultValues: {
      kpiDefinitionId: kpiId,
      kpiName: kpiTagname,
      kpiMainEquipment: kpiTagname,
      unit: kpiUnit ? {id: kpiUnit, label: kpiUnit} : null,

      aggregationType:
        kpiUnit && kpiUnit.includes('/')
          ? aggregationTypes.find((option) => option.id === AggregationType.WeightAverage) ?? null
          : kpiAggregationType
            ? aggregationTypes.find((option) => option.id === kpiAggregationType) ?? null
            : null,
      weightKpi: weightKpi
        ? weightKpiOptions.find((option) => option.id === String(weightKpi)) ?? null
        : null,
      kpiVisibilityFlags: kpiVisibilityFlags ?? []
    }
  })
  const aggregationType = useWatch({control, name: 'aggregationType'})

  const handleAddCustomUnit = (customUnit: string) => {
    if (!customUnit) {
      setUnits(mapUnitsToOptions(unitsList))
      return
    }
    const newUnit = {id: customUnit.toLowerCase(), label: customUnit}
    setUnits([...mapUnitsToOptions(unitsList), newUnit])
  }

  const submit = handleSubmit(({aggregationType, unit, kpiVisibilityFlags, weightKpi}) => {
    if (dirtyFields.unit && unit) {
      editKpiUnit(
        {unit: unit.id, kpiId, plantCode},
        {
          onError: () => reset(),
          onSuccess: () => {
            reset({unit}, {keepValues: true})
          }
        }
      )
    }
    if (dirtyFields.aggregationType && aggregationType) {
      editKpiAggregationType(
        {aggregationTypeId: aggregationType.id, kpiId, plantCode},
        {
          onError: () => reset(),
          onSuccess: () => {
            reset({aggregationType}, {keepValues: true})
          }
        }
      )
    }
    if (dirtyFields.weightKpi && weightKpi) {
      editKpiWeight(
        {weightKpiId: weightKpi.id, kpiId, plantCode},
        {
          onError: () => reset(),
          onSuccess: () => {
            reset({weightKpi}, {keepValues: true})
          }
        }
      )
    }
    if (dirtyFields.kpiVisibilityFlags) {
      editKpiVisibilityFlags(
        {kpiId, plantCode, kpiVisibilityFlags},
        {
          onError: () => reset(),
          onSuccess: () => {
            reset({kpiVisibilityFlags}, {keepValues: true})
          }
        }
      )
    }
  })

  const handleUnitChange = () => {
    if (unitsResponse.data && control._formValues.unit?.label?.includes('/')) {
      const weightAverageOption = aggregationTypes.find(
        (option) => option.id === AggregationType.WeightAverage
      )
      if (weightAverageOption) {
        setValue('aggregationType', weightAverageOption, {shouldDirty: true})
      }
    }
  }

  return (
    <KpiFormFields
      control={control}
      config={CONFIG}
      handleAddCustomUnit={handleAddCustomUnit}
      visibilityOptions={visibilityOptions}
      units={units}
      submit={submit}
      handleUnitChange={handleUnitChange}
      weightKpiOptions={weightKpiOptions}
      kpiTagname={kpiTagname}
      aggregationType={aggregationType}
      aggregationTypes={aggregationTypes}
    />
  )
}
